import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArticleLink from '@/components/feature/ArticleLink';
import { useArticleReader } from '@/components/feature/ArticleReaderProvider';
import { articlePath, formatThaiDate, getArticleBySlug, getCategoryBySlug } from '@/utils/content';
import { articleSchema, breadcrumbSchema, faqPageSchema, useSeo } from '@/hooks/useSeo';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const { openArticle } = useArticleReader();

  useEffect(() => {
    if (article) openArticle(article.slug);
  }, [article, openArticle]);

  const isPlatformArticle = article?.categorySlug === 'mt4-mt5';
  const isMt4 = Boolean(isPlatformArticle && article?.tags.includes('MT4'));
  const category = article ? getCategoryBySlug(article.categorySlug) : undefined;
  const categoryName = category?.name ?? article?.category ?? '';
  const categoryPath = category ? `/${category.slug}` : '/';
  const canonicalPath = `/${slug ?? ''}`;

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return [
      breadcrumbSchema([
        { name: 'หน้าแรก', path: '/' },
        { name: categoryName, path: categoryPath },
        { name: article.title, path: articlePath(article.slug) },
      ]),
      articleSchema({
        title: article.title,
        description: article.description,
        canonicalPath: articlePath(article.slug),
        image: article.heroImage,
        datePublished: article.updatedDate,
        dateModified: article.updatedDate,
        category: article.category,
        keywords: article.tags,
      }),
      ...(article.faqs.length > 0 ? [faqPageSchema(article.faqs)] : []),
    ];
  }, [article, categoryName, categoryPath]);

  useSeo({
    title: article ? `${article.title} | Forex Academy Thailand` : 'ไม่พบบทความ',
    description: article?.description ?? 'ไม่พบบทความที่ต้องการ',
    canonicalPath,
    image: article?.heroImage,
    jsonLd,
  });

  if (!article) return <NotFound />;

  const backHref = isPlatformArticle ? (isMt4 ? '/mt4-mt5#mt4-guide' : '/mt4-mt5#mt5-guide') : categoryPath;
  const backLabel = isPlatformArticle ? `กลับไปคู่มือ ${isMt4 ? 'MT4' : 'MT5'}` : `กลับไปหมวด ${categoryName}`;

  return (
    <div className="bg-background-50">
      <div className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="breadcrumb">
          <ol className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-700">
                หน้าแรก
              </Link>
            </li>
            <li aria-hidden="true" className="flex h-3 w-3 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </li>
            <li>
              <Link to={categoryPath} className="transition-colors hover:text-primary-700">
                {categoryName}
              </Link>
            </li>
            <li aria-hidden="true" className="flex h-3 w-3 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </li>
            <li aria-current="page" className="line-clamp-1 font-medium text-foreground-700">
              {article.title}
            </li>
          </ol>
        </nav>

        <span className="mt-6 inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
          <i className="ri-folder-line text-sm" />
          {article.category}
        </span>

        <h1 className="mt-3 text-balance font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-4xl">
          {article.title}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{article.description}</p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => openArticle(article.slug)}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-book-open-line text-base" />
            เปิดบทความฉบับเต็ม
          </button>
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-time-line text-sm" />
            อ่านประมาณ {article.readingTime}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-refresh-line text-sm" />
            อัปเดต{' '}
            <time dateTime={article.updatedDate}>{formatThaiDate(article.updatedDate)}</time>
          </span>
        </div>

        <div className="mt-8 flex flex-col gap-3">
          {article.intro.map((paragraph, index) => (
            <p key={index} className="text-sm leading-relaxed text-foreground-700 md:text-base">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-7">
          {article.sections.map((section) => (
            <section key={section.id}>
              <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">{section.heading}</h2>
              <div className="mt-3 flex flex-col gap-3">
                {section.paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-sm leading-relaxed text-foreground-700">
                    {paragraph}
                  </p>
                ))}
                {section.bullets.length > 0 && (
                  <ul className="flex flex-col gap-2">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-secondary-500" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
          <Link
            to={backHref}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-arrow-left-line text-base" />
            {backLabel}
          </Link>
        </div>

        {article.related.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading text-base font-semibold text-foreground-950">บทความที่เกี่ยวข้อง</h2>
            <ul className="mt-3 flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
              {article.related.map((relatedSlug) => {
                const related = getArticleBySlug(relatedSlug);
                if (!related) return null;
                return (
                  <li key={relatedSlug}>
                    <ArticleLink
                      to={articlePath(relatedSlug)}
                      className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-primary-50/60"
                    >
                      <span className="min-w-0 flex-1 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                        {related.title}
                      </span>
                      <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-300 transition-colors group-hover:text-primary-600" />
                    </ArticleLink>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}